import { format } from 'date-fns'

import { httpClient } from '@/services/http/httpClient'

export type ManualNotificationStatus = {
  userId: string
  technical: {
    canSend: boolean
    remaining: number
    sent: number
  }
  criteria: {
    canSend: boolean
    remaining: number
    sent: number
  }
}[]

export type IdeaApprovals = {
  id: string
  ideaId: string
  approvedBy: string
  description: string
  createdAt: string
  approvedByUser: {
    id: string
    name: string
    email: string
    image: string | null
  }
}

export type IdeaRecuse = {
  id: string
  ideaId: string
  recusedBy: string
  description: string
  createdAt: string
  recusedByUser: {
    id: string
    name: string
    email: string
    image: string | null
  }
}

type IdeaEvaluationHttpResponse = {
  avaliations: {
    kanbanStep: {
      id: string
      title: string
      usingCriteria: boolean
      hasReplicateEvaluators: boolean
      technicalReviewRequest: {
        id: string
        kanbanStepId: string
        deadline: string
        response?: string
        responseDate?: string
        message: string
        hasExpiredDeadline: boolean
        status: 'Technical review sent' | 'Technical review awaiting response'
        user: {
          id: string
          email: string
          name: string
          image?: string
          area?: {
            id: string
            name: string
          }
        }
      }[]
      score: string
      statusPerKanbanStep: 'Pendente' | 'Avaliado'
      evaluationCriteria: {
        id: string
        name: string
        description: string
        kanbanStepId: string
        maxEvaluators: number
        criterionWeight: number
        responseScale: {
          name: string
          weight: number
        }[]
        isMandatory: boolean
        campaignId: string
        criteriaEvaluations: {
          id: string
          userId: string
          evaluationName: string
          evaluationRate: number
        }[]
        evaluators: {
          id: string
          evaluator: {
            id: string
            email: string
            name: string
            image?: string
            status: 'ACTIVE' | 'INACTIVE'
            area?: {
              id: string
              name: string
            }
          }
          hasReplicateEvaluators: boolean
          evaluatorScore?: string
        }[]
        status: 'Avaliado' | 'Pendente'
      }[]
    }[]
    ideaApprovals: IdeaApprovals
    ideaRecuse: IdeaRecuse
    totalScore: string
    manualNotificationStatus: ManualNotificationStatus
  }
}

export type Evaluator = {
  id: string
  name: string
  image?: string
  area?: string
  hasReplicateEvaluators: boolean
  status: 'ACTIVE' | 'INACTIVE'
}

export type EvaluationTechnicalReview = {
  id: string
  stepId: string
  evaluator: Evaluator
  deadline: string
  hasExpiredDeadline: boolean
  message: string
  response?: string
  responseDate?: string
  status:
    | 'Technical review sent'
    | 'Technical review awaiting response'
    | 'Technical review expired'
}

type CriteriaAnswer = {
  id: string
  name: string
  description: string
  totalScore: number
  score: number
}

type CriteriaEvaluator = Evaluator & {
  evaluatorId: string
  criteriaAnswers: CriteriaAnswer[]
  averageScore?: string
}

export type EvaluationCriteria = {
  id: string
  name: string
  description: string
  mandatory: boolean
  weight: number
  status: 'Avaliado' | 'Pendente'
  scales: {
    name: string
    weight: number
  }[]
  analysis?: string
}

export type StepCriteria = {
  isDirectApproval: boolean
  usingCriteria: boolean
  hasEvaluators: boolean
  maxEvaluators: number
  evaluators: CriteriaEvaluator[]
  evaluations: EvaluationCriteria[]
}

export type Step = {
  id: string
  name: string
  score: string
  status?: 'Pendente' | 'Avaliado'
  statuses: {
    status: 'Pendente' | 'Avaliado'
    name: string
  }[]
  technicalReviews: EvaluationTechnicalReview[]
  hasReplicateEvaluators: boolean
  criteria: StepCriteria
}

export type IdeaEvaluation = {
  score: string
  steps: Step[]
  ideaApprovals: IdeaApprovals | null
  ideaRecuse: IdeaRecuse | null
  manualNotificationStatus: ManualNotificationStatus
}

export async function getIdeaEvaluation(id: string): Promise<IdeaEvaluation> {
  const { data } = await httpClient.get<IdeaEvaluationHttpResponse>(
    `ideas/evaluations/ideas/${id}`,
  )
  const { avaliations } = data

  return {
    score: avaliations.totalScore,
    steps: avaliations.kanbanStep.map((step) => {
      const [firstCriteria] = step.evaluationCriteria || []
      const maxEvaluators = firstCriteria?.maxEvaluators ?? 0

      const allAnswers = step.evaluationCriteria.flatMap((criteria) =>
        criteria.criteriaEvaluations.map((criteriaEvaluation) => ({
          id: criteriaEvaluation.id,
          userId: criteriaEvaluation.userId,
          name: criteria.name,
          description: criteria.description,
          totalScore: criteria.responseScale.length,
          score: criteriaEvaluation.evaluationRate,
        })),
      )

      const answersOfEvaluators = allAnswers.reduce<
        Record<string, CriteriaAnswer[]>
      >((acc, answer) => {
        if (!acc[answer.userId]) {
          acc[answer.userId] = []
        }

        acc[answer.userId].push(answer)

        return acc
      }, {})

      const evaluators: CriteriaEvaluator[] = step.evaluationCriteria.reduce(
        (acc, criteria) => {
          for (const evaluatorRelation of criteria.evaluators) {
            const evaluatorIndex = acc.findIndex(
              (accEvaluator) =>
                accEvaluator.id === evaluatorRelation.evaluator.id,
            )
            if (evaluatorIndex === -1) {
              acc.push({
                id: evaluatorRelation.evaluator.id,
                evaluatorId: evaluatorRelation.id,
                name: evaluatorRelation.evaluator.name,
                area: evaluatorRelation.evaluator.area?.name,
                image: evaluatorRelation.evaluator.image,
                status: evaluatorRelation.evaluator.status,
                hasReplicateEvaluators:
                  evaluatorRelation.hasReplicateEvaluators,
                criteriaAnswers:
                  answersOfEvaluators[evaluatorRelation.evaluator.id] ?? [],
                averageScore: evaluatorRelation.evaluatorScore,
              } as CriteriaEvaluator)
            }
          }

          return acc
        },
        [] as CriteriaEvaluator[],
      )

      const hasEvaluationCriteria =
        step.evaluationCriteria.length > 0 &&
        step.evaluationCriteria.find(
          (criteria) => criteria.evaluators.length > 0,
        )
      const hasTechnicalReview = step.technicalReviewRequest.length > 0

      const evaluationCriteriaStatus = step.evaluationCriteria.some(
        (criteria) => {
          const allEvaluatorsHaveEvaluated = criteria.evaluators.every(
            (evaluator) => {
              const evaluatorAnswers = allAnswers.filter(
                (answer) => answer.userId === evaluator.evaluator.id,
              )
              return evaluatorAnswers.length > 0
            },
          )
          return !allEvaluatorsHaveEvaluated
        },
      )
        ? 'Pendente'
        : 'Avaliado'

      const technicalReviewStatus = step.technicalReviewRequest.some(
        (technicalReview) => !technicalReview.response,
      )
        ? 'Pendente'
        : 'Avaliado'

      const status =
        hasEvaluationCriteria || hasTechnicalReview
          ? evaluationCriteriaStatus === 'Avaliado' &&
            technicalReviewStatus === 'Avaliado'
            ? ('Avaliado' as const)
            : ('Pendente' as const)
          : undefined

      const statuses = [
        ...(hasEvaluationCriteria
          ? [
              {
                status: evaluationCriteriaStatus as 'Pendente' | 'Avaliado',
                name:
                  evaluationCriteriaStatus === 'Avaliado'
                    ? 'Avaliado: Revisão de critérios'
                    : 'Pendente: Revisão de critérios',
              },
            ]
          : []),

        ...(hasTechnicalReview
          ? [
              {
                status: technicalReviewStatus as 'Pendente' | 'Avaliado',
                name:
                  technicalReviewStatus === 'Avaliado'
                    ? 'Avaliado: Revisão técnica'
                    : 'Pendente: Revisão técnica',
              },
            ]
          : []),
      ]
      const hasEvaluators = step.evaluationCriteria.some(
        (criteria) => criteria.evaluators.length > 0,
      )

      const evaluations = step.evaluationCriteria.map((criteria) => {
        return {
          id: criteria.id,
          name: criteria.name,
          description: criteria.description,
          scales: criteria.responseScale,
          weight: criteria.criterionWeight,
          status: criteria.status,
          mandatory: criteria.isMandatory,
        } as EvaluationCriteria
      })

      const technicalReviews = step.technicalReviewRequest.map(
        (technicalReview) => {
          return {
            id: technicalReview.id,
            stepId: technicalReview.kanbanStepId,
            evaluator: {
              id: technicalReview.user.id,
              name: technicalReview.user.name,
              area: technicalReview.user.area?.name,
              image: technicalReview.user.image,
            },
            deadline: format(
              new Date(technicalReview.deadline.replace('Z', '')),
              'dd/MM/yyyy',
            ),
            hasExpiredDeadline: technicalReview.hasExpiredDeadline,
            message: technicalReview.message,
            response: technicalReview.response,
            ...(technicalReview.responseDate && {
              responseDate: format(
                new Date(technicalReview.responseDate.replace('Z', '')),
                'dd/MM/yyyy',
              ),
            }),
            status: technicalReview.status,
          } as EvaluationTechnicalReview
        },
      )

      return {
        id: step.id,
        name: step.title,
        score: step.score,
        criteria: {
          isDirectApproval: false,
          usingCriteria: step.usingCriteria,
          hasEvaluators,
          maxEvaluators,
          evaluators,
          evaluations,
        },
        status,
        statuses,
        technicalReviews,
        statusPerKanbanStep: step.statusPerKanbanStep,
        hasReplicateEvaluators: step.hasReplicateEvaluators,
      }
    }),
    ideaApprovals: avaliations.ideaApprovals,
    ideaRecuse: avaliations.ideaRecuse,
    manualNotificationStatus: avaliations.manualNotificationStatus,
  }
}
